<template>
  <div class="ln-page">
    <div class="ln-wrap">
      <header class="ln-head" data-reveal>
        <p class="ln-eyebrow">Dashboard</p>
        <h1 class="ln-display">Integrations</h1>
        <p class="ln-lede">
          Connect 1LINK.IO to your tools: automate with Zapier, call the REST
          API directly, or let an AI agent manage your links through MCP.
        </p>
      </header>

      <div class="ln-sections">
        <!-- API Keys -->
        <section v-if="canWrite" class="ln-panel ln-section" data-reveal>
          <div class="ln-section__head">
            <v-icon class="ln-section__icon">mdi-key-variant</v-icon>
            <h2 class="ln-section__title">API keys</h2>
            <a
              class="ln-inline-link ln-section__head-link"
              href="https://api.1link.io/swagger"
              target="_blank"
              rel="noopener"
              >API documentation</a
            >
          </div>
          <div class="ln-section__body">
            <p class="ln-section__lede">
              Used to access the 1LINK.IO API programmatically. Keep these
              private.
            </p>

            <div v-if="apiKeyList.length" class="ln-keylist">
              <div
                v-for="thisApiKeyData in apiKeyList"
                :key="thisApiKeyData.api_key"
                class="ln-keyrow"
              >
                <div class="ln-keyrow__text">
                  <!-- ph-no-capture keeps every element that shows a key out of
                       PostHog events and session replays -->
                  <code class="ln-keyrow__key ph-no-capture">{{
                    thisApiKeyData.api_key
                  }}</code>
                  <div class="ln-keyrow__desc">
                    {{ thisApiKeyData.description || "No description" }}
                  </div>
                </div>
                <div class="ln-actions">
                  <v-tooltip location="bottom">
                    <template v-slot:activator="{ props }">
                      <button
                        type="button"
                        class="ln-iconbtn"
                        aria-label="Copy API key"
                        v-bind="props"
                        @click="copyApiKey(thisApiKeyData.api_key)"
                      >
                        <v-icon size="small">{{
                          copiedApiKey === thisApiKeyData.api_key
                            ? "mdi-check"
                            : "mdi-content-copy"
                        }}</v-icon>
                      </button>
                    </template>
                    <span>Copy to clipboard</span>
                  </v-tooltip>
                  <v-tooltip location="bottom">
                    <template v-slot:activator="{ props }">
                      <button
                        type="button"
                        class="ln-iconbtn ln-iconbtn--danger"
                        aria-label="Delete API key"
                        v-bind="props"
                        @click="deleteApiKey(thisApiKeyData.api_key)"
                      >
                        <v-icon size="small">mdi-delete</v-icon>
                      </button>
                    </template>
                    <span>Delete</span>
                  </v-tooltip>
                </div>
              </div>
            </div>
            <p v-else class="ln-keylist-empty">
              No API keys yet. Create one to call the API or connect an AI
              agent.
            </p>
          </div>
          <div class="ln-section__footer">
            <button
              type="button"
              class="ln-btn ln-btn--quiet"
              @click="createNewApiKey"
            >
              <v-icon size="small" class="ln-btn__icon">mdi-plus</v-icon>
              Create new API key
            </button>
          </div>
        </section>

        <!-- AI Agents (MCP) -->
        <section class="ln-panel ln-section" data-reveal>
          <div class="ln-section__head">
            <v-icon class="ln-section__icon">mdi-robot-outline</v-icon>
            <h2 class="ln-section__title">AI agents (MCP)</h2>
          </div>
          <div class="ln-section__body">
            <p class="ln-section__lede">
              Connect 1LINK.IO to Claude, Cursor and other AI agents with the
              Model Context Protocol, so they can create and manage your links
              for you.
            </p>

            <p class="ln-field-label">Server URL</p>
            <div class="ln-kv">
              <div class="ln-kv__row">
                <code class="ln-kv__value">{{ mcpUrl }}</code>
                <button
                  type="button"
                  class="ln-iconbtn"
                  aria-label="Copy server URL"
                  @click="copyServerUrl"
                >
                  <v-icon size="small">{{
                    copiedServerUrl ? "mdi-check" : "mdi-content-copy"
                  }}</v-icon>
                </button>
              </div>
            </div>

            <p class="ln-section__note">
              Authentication uses the <code>x-api-key</code> header with one of
              your API keys above.
              <span v-if="!firstApiKey" class="ln-section__note--warn"
                >Create an API key first.</span
              >
            </p>

            <div class="ln-accordion">
              <v-expansion-panels flat>
                <v-expansion-panel>
                  <v-expansion-panel-title>
                    <span class="ln-accordion__label">
                      <v-icon size="small" class="ln-accordion__icon"
                        >mdi-auto-fix</v-icon
                      >
                      Let your AI agent set it up
                    </span>
                  </v-expansion-panel-title>
                  <v-expansion-panel-text>
                    <p class="ln-section__note">
                      Paste this prompt in Claude, Cursor or any other agent
                      that can edit files, it will configure itself:
                    </p>
                    <pre class="ln-code ph-no-capture">{{ setupPrompt }}</pre>
                    <button
                      type="button"
                      class="ln-btn ln-btn--quiet ln-btn--sm"
                      @click="copySetupPrompt"
                    >
                      <v-icon size="small" class="ln-btn__icon">{{
                        promptCopied ? "mdi-check" : "mdi-content-copy"
                      }}</v-icon>
                      {{ promptCopied ? "Copied" : "Copy prompt" }}
                    </button>
                  </v-expansion-panel-text>
                </v-expansion-panel>

                <v-expansion-panel>
                  <v-expansion-panel-title>
                    <span class="ln-accordion__label">
                      <v-icon size="small" class="ln-accordion__icon"
                        >mdi-console-line</v-icon
                      >
                      Claude Code
                    </span>
                  </v-expansion-panel-title>
                  <v-expansion-panel-text>
                    <p class="ln-section__note">Run this in your terminal:</p>
                    <pre class="ln-code ph-no-capture">{{
                      snippets.claudeCode
                    }}</pre>
                    <p class="ln-section__note">
                      Then check it with <code>/mcp</code> inside Claude Code.
                    </p>
                  </v-expansion-panel-text>
                </v-expansion-panel>

                <v-expansion-panel>
                  <v-expansion-panel-title>
                    <span class="ln-accordion__label">
                      <v-icon size="small" class="ln-accordion__icon"
                        >mdi-monitor</v-icon
                      >
                      Claude (web and desktop)
                    </span>
                  </v-expansion-panel-title>
                  <v-expansion-panel-text>
                    <ol class="ln-steps">
                      <li>Open <strong>Customize &gt; Connectors</strong></li>
                      <li>Click <strong>Add custom connector</strong></li>
                      <li>Enter the server URL above</li>
                      <li>
                        Open <strong>Request headers</strong>, choose
                        <code>x-api-key</code> and paste your API key as the
                        value
                      </li>
                      <li>Click <strong>Add</strong></li>
                    </ol>
                    <p class="ln-section__note">
                      Request header authentication is currently being rolled
                      out by Anthropic. If you do not see the
                      <strong>Request headers</strong> section yet, use Claude
                      Code instead.
                    </p>
                  </v-expansion-panel-text>
                </v-expansion-panel>

                <v-expansion-panel>
                  <v-expansion-panel-title>
                    <span class="ln-accordion__label">
                      <v-icon size="small" class="ln-accordion__icon"
                        >mdi-code-tags</v-icon
                      >
                      Cursor
                    </span>
                  </v-expansion-panel-title>
                  <v-expansion-panel-text>
                    <p class="ln-section__note">
                      Add it to <code>~/.cursor/mcp.json</code> (or
                      <code>.cursor/mcp.json</code> for a single project):
                    </p>
                    <pre class="ln-code ph-no-capture">{{
                      snippets.cursor
                    }}</pre>
                    <p class="ln-section__note">
                      If you use the project-level file, add it to
                      <code>.gitignore</code> — it will contain your API key.
                    </p>
                  </v-expansion-panel-text>
                </v-expansion-panel>

                <v-expansion-panel>
                  <v-expansion-panel-title>
                    <span class="ln-accordion__label">
                      <v-icon size="small" class="ln-accordion__icon"
                        >mdi-microsoft-visual-studio-code</v-icon
                      >
                      VS Code (GitHub Copilot)
                    </span>
                  </v-expansion-panel-title>
                  <v-expansion-panel-text>
                    <p class="ln-section__note">
                      Add it to <code>.vscode/mcp.json</code>:
                    </p>
                    <pre class="ln-code ph-no-capture">{{
                      snippets.vscode
                    }}</pre>
                    <p class="ln-section__note">
                      Add <code>.vscode/mcp.json</code> to
                      <code>.gitignore</code> — it will contain your API key.
                    </p>
                  </v-expansion-panel-text>
                </v-expansion-panel>

                <v-expansion-panel>
                  <v-expansion-panel-title>
                    <span class="ln-accordion__label">
                      <v-icon size="small" class="ln-accordion__icon"
                        >mdi-chat-outline</v-icon
                      >
                      ChatGPT
                    </span>
                  </v-expansion-panel-title>
                  <v-expansion-panel-text>
                    <p class="ln-section__note">
                      Not supported yet. ChatGPT custom connectors only
                      authenticate with OAuth and cannot send an API key header,
                      so the server cannot identify your account. Contact us at
                      <a href="mailto:info@1link.io">info@1link.io</a> if you
                      need it.
                    </p>
                  </v-expansion-panel-text>
                </v-expansion-panel>

                <v-expansion-panel>
                  <v-expansion-panel-title>
                    <span class="ln-accordion__label">
                      <v-icon size="small" class="ln-accordion__icon"
                        >mdi-dots-horizontal-circle-outline</v-icon
                      >
                      Any other MCP client
                    </span>
                  </v-expansion-panel-title>
                  <v-expansion-panel-text>
                    <p class="ln-section__note">
                      The server speaks streamable HTTP, most clients accept
                      this configuration:
                    </p>
                    <pre class="ln-code ph-no-capture">{{
                      snippets.generic
                    }}</pre>
                    <p class="ln-section__note">
                      Some clients name this transport
                      <code>streamable-http</code> instead of <code>http</code>.
                    </p>
                    <p class="ln-section__note">
                      If your client stores this in a project file, keep it out
                      of version control — it will contain your API key.
                    </p>
                  </v-expansion-panel-text>
                </v-expansion-panel>
              </v-expansion-panels>
            </div>
          </div>
        </section>

        <!-- Zapier -->
        <section class="ln-panel ln-section" data-reveal>
          <div class="ln-section__head">
            <v-icon class="ln-section__icon">mdi-lightning-bolt-outline</v-icon>
            <h2 class="ln-section__title">Zapier</h2>
          </div>
          <div class="ln-section__body">
            <p class="ln-section__lede">
              Create and manage links directly from a Zapier workflow, no code
              required.
            </p>
            <a
              class="ln-btn ln-btn--solid"
              href="https://zapier.com/developer/invite/91269/a7d4edc6cc41d30408bdda77fb0223d0/"
              target="_blank"
              rel="noopener"
            >
              Get the Zap
              <v-icon size="small" class="ln-btn__icon ln-btn__icon--right"
                >mdi-chevron-right</v-icon
              >
            </a>
          </div>
        </section>
      </div>
    </div>
  </div>
</template>
<script>
import {
  listApiKeys,
  createApiKey,
  deleteApiKey,
  handleApiError,
} from "../api";
import { mapGetters } from "vuex";

export default {
  data() {
    return {
      apiKeyList: [],
      mcpUrl: "https://api.1link.io/v1/mcp",
      promptCopied: false,
      copiedApiKey: null,
      copiedServerUrl: false,
    };
  },
  computed: {
    ...mapGetters(["canWrite"]),
    firstApiKey() {
      return this.apiKeyList.length > 0 ? this.apiKeyList[0].api_key : null;
    },
    // use a real api key in the examples so they can be copied as they are
    exampleApiKey() {
      return this.firstApiKey || "YOUR_API_KEY";
    },
    snippets() {
      const url = this.mcpUrl;
      const apiKey = this.exampleApiKey;

      // kept as strings so the indentation of the snippets is not reformatted
      const jsonConfig = (urlField, type) =>
        [
          "{",
          '  "mcpServers": {',
          '    "1link": {',
          ...(type ? [`      "type": "${type}",`] : []),
          `      "${urlField}": "${url}",`,
          '      "headers": {',
          `        "x-api-key": "${apiKey}"`,
          "      }",
          "    }",
          "  }",
          "}",
        ].join("\n");

      return {
        claudeCode: `claude mcp add --transport http 1link ${url} \\\n  --header "x-api-key: ${apiKey}"`,
        cursor: jsonConfig("url"),
        generic: jsonConfig("url", "http"),
        vscode: [
          "{",
          '  "servers": {',
          '    "1link": {',
          '      "type": "http",',
          `      "url": "${url}",`,
          '      "headers": {',
          `        "x-api-key": "${apiKey}"`,
          "      }",
          "    }",
          "  }",
          "}",
        ].join("\n"),
      };
    },
    // prompt the user can paste in any agent to let it configure itself
    setupPrompt() {
      const url = this.mcpUrl;
      const apiKey = this.exampleApiKey;

      return [
        "Set up the 1LINK.IO MCP server so you can create and manage my short links.",
        "",
        `Server URL: ${url}`,
        "Transport: streamable HTTP",
        `Authentication: send the header "x-api-key: ${apiKey}" on every request`,
        "",
        "Add it using your own MCP configuration format. The API key is a secret: don't print it back to me or commit it. If a \"1link\" server is already configured, update it instead of adding a second one, and don't touch any other MCP server.",
        "",
        "When you're done, tell me how to reload so it connects, then confirm the tools are available.",
      ].join("\n");
    },
  },
  mounted() {
    this.loadApiKeys().finally(() => {
      this.$store.commit("setIsLoading", false);
    });
    this.setupReveal();
  },
  beforeUnmount() {
    if (this._revealObserver) {
      this._revealObserver.disconnect();
      this._revealObserver = null;
    }
  },
  methods: {
    setupReveal() {
      const elements = this.$el.querySelectorAll("[data-reveal]");
      const prefersReducedMotion =
        typeof window.matchMedia === "function" &&
        window.matchMedia("(prefers-reduced-motion: reduce)").matches;

      // no observer (or the user asked for no motion): show everything at once
      // rather than leaving the page permanently at opacity 0
      if (!("IntersectionObserver" in window) || prefersReducedMotion) {
        elements.forEach((el) => el.classList.add("is-revealed"));
        return;
      }

      this._revealObserver = new IntersectionObserver(
        (entries, observer) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            entry.target.classList.add("is-revealed");
            observer.unobserve(entry.target);
          });
        },
        { threshold: 0.05 },
      );
      elements.forEach((el) => this._revealObserver.observe(el));
    },
    async selectAll() {
      this.$refs.input.select();
    },
    copyApiKey(apiKey) {
      navigator.clipboard
        .writeText(apiKey)
        .then(() => {
          this.copiedApiKey = apiKey;
          setTimeout(() => {
            if (this.copiedApiKey === apiKey) {
              this.copiedApiKey = null;
            }
          }, 2000);
        })
        .catch((err) => {
          console.error("Failed to copy API key: ", err);
        });
    },
    copyServerUrl() {
      navigator.clipboard
        .writeText(this.mcpUrl)
        .then(() => {
          this.copiedServerUrl = true;
          setTimeout(() => {
            this.copiedServerUrl = false;
          }, 2000);
        })
        .catch((err) => {
          console.error("Failed to copy server URL: ", err);
        });
    },
    async copySetupPrompt() {
      try {
        await navigator.clipboard.writeText(this.setupPrompt);
      } catch (err) {
        window.alert("Could not copy, please select the prompt manually");
        return;
      }

      this.promptCopied = true;
      setTimeout(() => {
        this.promptCopied = false;
      }, 2000);
    },
    /** the role is only known once GET /user answered: a viewer neither loads nor sees keys */
    async loadApiKeys() {
      try {
        await this.$store.dispatch("getUserPlan");
      } catch (err) {
        // the role stays unknown and reads as admin: an admin still gets the keys, and the API
        // refuses a viewer's key list anyway
        handleApiError(err);
      }
      if (this.canWrite) {
        await this.fetchApiKeys();
      }
    },
    async fetchApiKeys() {
      const apiKeyList = await listApiKeys();
      this.apiKeyList = apiKeyList.result || [];
    },
    async createNewApiKey() {
      const apiKeyDescription = window.prompt(
        "You can enter an optional note/description for this API Key",
      );

      // null if user click Cancel
      if (apiKeyDescription === null) {
        return;
      }

      // converts "" -> undefined
      const description = apiKeyDescription || undefined;

      let operationPerformed = false;
      try {
        await createApiKey(description);

        operationPerformed = true;
      } catch (err) {
        window.alert(
          "Failed to create API KEY, please try again, if the error persists contact support",
        );
      }

      await this.fetchApiKeys();

      if (operationPerformed) {
        window.alert("Api key created successfully");
      }
    },
    async deleteApiKey(apiKey) {
      const confirmDelete = window.confirm(
        `Are you sure you want to delete the api key "${apiKey}" ? This action is irreversible and will impact any services that is using this api key.`,
      );

      if (confirmDelete !== true) {
        return;
      }

      let operationPerformed = false;
      try {
        await deleteApiKey(apiKey);
        operationPerformed = true;
      } catch (err) {
        window.alert(
          "Failed to delete API KEY, please try again, if the error persists contact support",
        );
      }

      await this.fetchApiKeys();

      if (operationPerformed) {
        window.alert("Api key deleted successfully");
      }
    },
  },
};
</script>
<style scoped>
/* ---------- canvas ---------------------------------------------------- */

.ln-page {
  min-height: 100%;
  background: var(--ln-canvas);
}

.ln-wrap {
  padding: 20px 16px 40px;
}

/* ---------- scroll entry ---------------------------------------------- */

[data-reveal] {
  opacity: 0;
  transform: translateY(12px);
  transition:
    opacity 600ms var(--ln-ease),
    transform 600ms var(--ln-ease);
}

[data-reveal].is-revealed {
  opacity: 1;
  transform: none;
}

.ln-sections [data-reveal]:nth-child(2) {
  transition-delay: 80ms;
}

.ln-sections [data-reveal]:nth-child(3) {
  transition-delay: 160ms;
}

@media (prefers-reduced-motion: reduce) {
  [data-reveal],
  [data-reveal].is-revealed {
    opacity: 1;
    transform: none;
    transition: none;
  }
}

/* ---------- page header ------------------------------------------------ */

.ln-head {
  margin-bottom: 24px;
}

.ln-eyebrow {
  margin: 0 0 10px;
  font-family: var(--ln-mono);
  font-size: 10px;
  font-weight: 500;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--ln-muted);
}

.ln-display {
  margin: 0;
  font-family: sans-serif;
  font-size: 40px;
  font-weight: 400;
  line-height: 1.1;
  letter-spacing: -0.025em;
  color: var(--ln-ink);
}

.ln-lede {
  margin: 10px 0 0;
  max-width: 52ch;
  font-size: 14px;
  line-height: 1.6;
  color: var(--ln-muted);
}

/* ---------- buttons ---------------------------------------------------- */

.ln-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  padding: 0 16px;
  height: 38px;
  border: 1px solid transparent;
  border-radius: var(--ln-radius-sm);
  background: transparent;
  font-family: sans-serif;
  font-size: 13px;
  font-weight: 500;
  letter-spacing: 0;
  line-height: 1;
  text-decoration: none;
  white-space: nowrap;
  cursor: pointer;
  transition:
    background-color 200ms var(--ln-ease),
    border-color 200ms var(--ln-ease),
    color 200ms var(--ln-ease),
    transform 120ms var(--ln-ease);
}

.ln-btn:active {
  transform: scale(0.98);
}

.ln-btn:focus-visible {
  outline: 2px solid var(--ln-ink);
  outline-offset: 2px;
}

.ln-btn__icon.v-icon {
  font-size: 15px;
  color: inherit;
}

.ln-btn__icon--right {
  margin-left: -2px;
}

.ln-btn--solid {
  background: var(--ln-accent);
  color: #ffffff;
}

.ln-btn--solid:hover {
  background: var(--ln-accent-hover);
}

.ln-btn--quiet {
  border-color: var(--ln-line);
  background: var(--ln-surface);
  color: var(--ln-ink-2);
}

.ln-btn--quiet:hover {
  background: var(--ln-surface-2);
  border-color: #dcdcdc;
}

.ln-btn--sm {
  height: 28px;
  padding: 0 12px;
  font-size: 12px;
}

.ln-inline-link {
  font-size: 12px;
  font-weight: 500;
  color: var(--ln-ink-2);
  text-decoration: none;
  border-bottom: 1px solid var(--ln-line);
  transition:
    border-color 200ms var(--ln-ease),
    color 200ms var(--ln-ease);
}

.ln-inline-link:hover {
  color: var(--ln-ink);
  border-bottom-color: var(--ln-ink);
}

/* ---------- sections ---------------------------------------------------- */

.ln-sections {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.ln-panel {
  border: 1px solid var(--ln-line);
  border-radius: var(--ln-radius);
  background: var(--ln-surface);
  overflow: hidden;
}

.ln-section__head {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 18px 20px;
  border-bottom: 1px solid var(--ln-line);
}

.ln-section__icon.v-icon {
  font-size: 19px;
  color: var(--ln-muted);
}

.ln-section__title {
  margin: 0;
  flex: 1 1 auto;
  font-family: sans-serif;
  font-size: 17px;
  font-weight: 500;
  letter-spacing: -0.01em;
  color: var(--ln-ink);
}

.ln-section__head-link {
  flex: 0 0 auto;
}

.ln-section__body {
  padding: 20px;
}

.ln-section__lede {
  margin: 0 0 16px;
  max-width: 60ch;
  font-size: 14px;
  line-height: 1.6;
  color: var(--ln-ink-2);
}

.ln-section__note {
  display: block;
  margin: 12px 0 0;
  font-size: 12.5px;
  line-height: 1.6;
  color: var(--ln-muted);
}

.ln-section__note:first-child {
  margin-top: 0;
}

.ln-section__note--warn {
  color: var(--ln-red-fg);
  font-weight: 500;
}

.ln-section__body a:not(.ln-btn) {
  color: var(--ln-accent);
}

.ln-section__footer {
  display: flex;
  justify-content: flex-end;
  padding: 0 20px 20px;
}

/* ---------- inline code -------------------------------------------------- */

/* Vuetify 2 tinted every `code` element itself, which is what made these
   chips read as code; Vuetify 4 does not, and --ln-surface-2 (#FBFBFA) is
   too close to the white it sits on to register. */
.ln-section__body code,
.ln-accordion code {
  padding: 2px 5px;
  border-radius: 4px;
  background: rgba(0, 0, 0, 0.05);
  font-family: var(--ln-mono);
  font-size: 0.92em;
  color: var(--ln-ink);
}

.ln-code {
  margin: 6px 0 12px;
  padding: 12px 14px;
  border: 1px solid var(--ln-line);
  border-radius: var(--ln-radius-sm);
  background: var(--ln-surface-2);
  overflow-x: auto;
  font-family: var(--ln-mono);
  font-size: 12.5px;
  line-height: 1.6;
  color: var(--ln-ink);
  white-space: pre;
}

/* ---------- key/value block ---------------------------------------------- */

.ln-field-label {
  margin: 0 0 8px;
  font-family: var(--ln-mono);
  font-size: 10px;
  font-weight: 500;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--ln-muted);
}

.ln-kv {
  padding: 10px 12px;
  border: 1px solid var(--ln-line);
  border-radius: var(--ln-radius-sm);
  background: var(--ln-surface-2);
}

.ln-kv__row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

code.ln-kv__value {
  min-width: 0;
  padding: 0;
  background: rgba(0, 0, 0, 0.05);
  font-family: var(--ln-mono);
  font-size: 12.5px;
  color: var(--ln-ink);
  overflow-wrap: anywhere;
}

/* ---------- api key list --------------------------------------------------- */

.ln-keylist {
  border: 1px solid var(--ln-line);
  border-radius: var(--ln-radius-sm);
  overflow: hidden;
}

.ln-keyrow {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 12px 14px;
  background: var(--ln-surface);
}

.ln-keyrow + .ln-keyrow {
  border-top: 1px solid var(--ln-line);
}

.ln-keyrow__text {
  min-width: 0;
  flex: 1 1 auto;
}

code.ln-keyrow__key {
  display: inline-block;
  max-width: 100%;
  padding: 0;
  background: rgba(0, 0, 0, 0.05);
  font-family: var(--ln-mono);
  font-size: 12.5px;
  color: var(--ln-ink);
  overflow-wrap: anywhere;
}

.ln-keyrow__desc {
  margin-top: 3px;
  font-size: 12px;
  color: var(--ln-muted);
}

.ln-keylist-empty {
  margin: 0;
  padding: 16px;
  border: 1px dashed var(--ln-line);
  border-radius: var(--ln-radius-sm);
  font-size: 13px;
  line-height: 1.6;
  color: var(--ln-muted);
}

/* ---------- icon buttons ------------------------------------------------- */

.ln-iconbtn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  flex: 0 0 auto;
  border: 0;
  border-radius: var(--ln-radius-sm);
  background: transparent;
  color: var(--ln-muted);
  text-decoration: none;
  cursor: pointer;
  transition:
    background-color 200ms var(--ln-ease),
    color 200ms var(--ln-ease);
}

.ln-iconbtn:hover {
  background: var(--ln-surface);
  color: var(--ln-ink);
}

.ln-iconbtn:focus-visible {
  outline: 2px solid var(--ln-ink);
  outline-offset: 1px;
}

.ln-iconbtn--danger:hover {
  background: var(--ln-red-bg);
  color: var(--ln-red-fg);
}

.ln-iconbtn .v-icon {
  font-size: 17px;
  color: inherit;
}

.ln-actions {
  display: flex;
  align-items: center;
  flex: 0 0 auto;
  gap: 2px;
}

/* ---------- accordion ------------------------------------------------------ */

.ln-accordion {
  margin-top: 20px;
  border: 1px solid var(--ln-line);
  border-radius: var(--ln-radius-sm);
  overflow: hidden;
}

.ln-accordion :deep(.v-expansion-panel) {
  background: var(--ln-surface);
  color: inherit;
}

.ln-accordion :deep(.v-expansion-panel__shadow) {
  box-shadow: none;
}

.ln-accordion :deep(.v-expansion-panel:not(:first-child)) {
  border-top: 1px solid var(--ln-line);
}

.ln-accordion :deep(.v-expansion-panel-title) {
  min-height: 48px;
  padding: 14px 18px;
  font-family: sans-serif;
  font-size: 13.5px;
  font-weight: 500;
  color: var(--ln-ink);
}

.ln-accordion :deep(.v-expansion-panel-title--active) {
  color: var(--ln-ink);
}

.ln-accordion :deep(.v-expansion-panel-title__icon .v-icon) {
  color: var(--ln-muted) !important;
  font-size: 20px;
}

.ln-accordion__label {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.ln-accordion__icon.v-icon {
  font-size: 16px;
  color: var(--ln-muted);
}

.ln-accordion :deep(.v-expansion-panel-text__wrapper) {
  padding: 0 18px 18px;
  font-size: 13px;
  line-height: 1.6;
  color: var(--ln-ink-2);
}

.ln-steps {
  margin: 0 0 12px;
  padding-left: 18px;
  font-size: 13px;
  line-height: 1.7;
  color: var(--ln-ink-2);
}

.ln-steps li {
  margin-bottom: 4px;
}

.ln-steps li:last-child {
  margin-bottom: 0;
}

/* ---------- breakpoints ------------------------------------------------ */

@media (min-width: 600px) {
  .ln-wrap {
    padding: 28px 20px 56px;
  }

  .ln-display {
    font-size: 52px;
  }

  .ln-section__head {
    padding: 20px 26px;
  }

  .ln-section__body {
    padding: 26px;
  }

  .ln-section__footer {
    padding: 0 26px 26px;
  }
}

@media (min-width: 960px) {
  .ln-wrap {
    padding: 36px 24px 64px;
  }
}
</style>
